import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, Link2, Mail, MessageCircle, Share2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { Button, Input, Sheet, SheetBody, SheetFooter, SheetHeader, Textarea } from '@/features/common/ui';
import { whatsappNumber } from './share';

/**
 * Getting the invoice to the customer: WhatsApp, email, a link.
 *
 * ── WHAT EACH BUTTON DOES ──
 * On a phone, WhatsApp and "Share…" hand the PDF ITSELF to the system share
 * sheet (the Web Share API with a file), so WhatsApp gets the document the
 * way it would from Files - the customer sees a PDF, not a link. Where the
 * browser cannot share a file (a laptop, an older phone), WhatsApp opens on
 * the customer's number with the message and the public link to the PDF.
 * Email goes from the server with the PDF attached, to the customer's
 * address or the one typed; when the server has no mail set up it says so
 * and offers the same message as a mailto. Copy link is the link alone.
 *
 * The message, the link and the contact all come from ONE server call
 * (`/share`), so the sentence the customer reads is the same on every path
 * and carries the letterhead's payment details.
 */

interface ShareMessage {
  text: string;
  subject: string;
  customerPhone: string | null;
  customerEmail: string | null;
  url: string;
  filename: string;
}

/** The PDF as a File, fetched with the session - for the share sheet. */
async function fetchPdf(invoiceId: string, orgId: string, filename: string): Promise<File> {
  const res = await fetch(`/v1/invoices/${invoiceId}/pdf`, { credentials: 'include', headers: { 'x-org-id': orgId } });
  if (!res.ok) throw new Error('The PDF could not be fetched');
  return new File([await res.blob()], filename, { type: 'application/pdf' });
}

function canShareFiles(): boolean {
  try {
    const probe = new File(['x'], 'probe.pdf', { type: 'application/pdf' });
    return typeof navigator !== 'undefined' && !!navigator.share && !!navigator.canShare && navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export function ShareInvoiceSheet({ invoiceId, number, onClose }: { invoiceId: string; number: string; onClose: () => void }) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const [to, setTo] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);

  const share = useQuery({
    queryKey: ['invoice-share', org?.id, invoiceId],
    queryFn: () => api.get<ShareMessage>(`/invoices/${invoiceId}/share`),
    enabled: !!org?.id,
  });
  const data = share.data;
  const email = useMutation({
    mutationFn: () => api.post<{ delivered: boolean; to: string }>(`/invoices/${invoiceId}/email`, { to: to ?? data?.customerEmail ?? undefined, message: message || undefined }),
    onSuccess: (r) => setEmailedTo(r.to),
  });
  const emailError = email.error instanceof ApiError ? email.error : undefined;
  const mailNotSetUp = !!emailError && /not set up/i.test(emailError.message);
  const filesShareable = canShareFiles();

  const viaSystemShare = async () => {
    if (!data || !org) return;
    setShareError(null);
    try {
      const file = await fetchPdf(invoiceId, org.id, data.filename);
      await navigator.share({ files: [file], title: data.subject, text: data.text });
    } catch (e) {
      // The person closing the sheet is not an error worth a sentence.
      if ((e as { name?: string })?.name !== 'AbortError') setShareError('The share sheet could not open. Use the link instead.');
    }
  };

  const viaWhatsApp = async () => {
    if (!data) return;
    if (filesShareable) return viaSystemShare();
    const number = whatsappNumber(data.customerPhone);
    const url = `https://wa.me/${number ?? ''}?text=${encodeURIComponent(data.text)}`;
    window.open(url, '_blank', 'noopener');
  };

  const copyLink = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShareError('Copy did not work here - select the link and copy it.');
    }
  };

  const shareErrorText = share.error instanceof ApiError ? share.error.message : share.isError ? 'The share details could not be loaded.' : null;
  const toValue = to ?? data?.customerEmail ?? '';

  return (
    <Sheet label={`Share ${number}`} onClose={onClose} testId="share-invoice">
      <SheetHeader title={`Share ${number}`} onClose={onClose} />
      <SheetBody>
        {shareErrorText ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {shareErrorText}
          </p>
        ) : null}

        <div className="grid gap-2 sm:grid-cols-2">
          <Button type="button" onClick={() => { viaWhatsApp().catch(() => undefined); }} disabled={!data} data-testid="share-whatsapp" className="justify-start">
            <MessageCircle className="size-4" aria-hidden />
            WhatsApp{data?.customerPhone ? ` ${data.customerPhone}` : ''}
          </Button>
          {filesShareable ? (
            <Button type="button" variant="outline" onClick={() => { viaSystemShare().catch(() => undefined); }} disabled={!data} data-testid="share-system" className="justify-start">
              <Share2 className="size-4" aria-hidden />
              Share the PDF…
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => { copyLink().catch(() => undefined); }} disabled={!data} data-testid="share-copy" className="justify-start">
              {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
              {copied ? 'Link copied' : 'Copy link'}
            </Button>
          )}
        </div>
        {!filesShareable && data && !data.customerPhone ? (
          <p className="text-xs text-muted-foreground">The customer has no phone number on file, so WhatsApp opens on your contacts; pick one there.</p>
        ) : null}
        {shareError ? <p className="text-xs text-destructive">{shareError}</p> : null}

        <div className="rounded-md border border-border p-3">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Mail className="size-4 text-muted-foreground" aria-hidden />
            Email the PDF
          </p>
          {emailedTo ? (
            <p className="mt-2 text-sm" role="status" data-testid="share-emailed">
              Sent to <span className="font-medium">{emailedTo}</span> with {data?.filename ?? 'the PDF'} attached.
            </p>
          ) : (
            <form
              className="mt-3 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                email.mutate();
              }}
            >
              <Input label="To" type="email" required value={toValue} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" data-testid="share-email-to" />
              <Textarea label="A line from you (optional)" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Thanks for your business - the invoice for this month is attached." />
              {emailError && !mailNotSetUp ? (
                <p role="alert" className="text-sm text-destructive">
                  {emailError.message}
                </p>
              ) : null}
              {mailNotSetUp && data ? (
                <p className="text-sm text-muted-foreground">
                  {emailError?.message}{' '}
                  <a
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    href={`mailto:${encodeURIComponent(toValue)}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.text)}`}
                    data-testid="share-mailto"
                  >
                    Open in your mail app instead
                  </a>
                </p>
              ) : null}
              <Button type="submit" variant="outline" size="sm" loading={email.isPending} disabled={!data || !toValue} data-testid="share-email-send">
                <Mail className="size-3.5" aria-hidden />
                Send email
              </Button>
            </form>
          )}
        </div>

        {data ? (
          <div className="rounded-md bg-muted/40 p-3">
            <p className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Link2 className="size-3.5" aria-hidden />
              The message
            </p>
            <pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground" data-testid="share-text">
              {data.text}
            </pre>
            <p className="mt-2 text-[11px] text-muted-foreground">
              The link opens the PDF without signing in. Anyone with the link can read this one invoice; nothing else.
            </p>
          </div>
        ) : null}
      </SheetBody>
      <SheetFooter className="justify-end">
        {filesShareable ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => { copyLink().catch(() => undefined); }} disabled={!data} data-testid="share-copy">
            {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
            {copied ? 'Link copied' : 'Copy link'}
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onClose}>
          Done
        </Button>
      </SheetFooter>
    </Sheet>
  );
}
